import { useRef, useState, useEffect } from 'preact/hooks'

import Icon from '@/components/snippets/icon/icon'

import { SearchInput } from 'libs/athos/components/quick-search/SearchInput'
import { QuickSearchDropdown } from 'libs/athos/components/quick-search/QuickSearchDropdown'
import { AthosAutocompleteProvider } from 'libs/athos/fetchers/autocomplete'
import { BeaconAnalyticsProvider } from 'libs/athos/hooks/useBeaconAnalytics'

function QuickSearch({ id = 'quick-search' }) {
  const searchInputRef = useRef()
  const [inputValue, setInputValue] = useState()
  const [query, setQuery] = useState()
  const closeButtonRef = useRef()
  const modalRef = useRef()
  const [redirect, setRedirect] = useState(false)

  const handleClose = (event) => {
    event.preventDefault()
    modalRef.current?.handleHide()
  }

  const handleUpdateValue = (event) => {
    setInputValue(event.target.value)
  }

  const resetInputValue = () => {
    setInputValue('')
    searchInputRef.current.value = ''
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(inputValue)
    }, 400)
    return () => {
      clearTimeout(timeout)
    }
  }, [inputValue])

  useEffect(() => {
    const modalEl = modalRef.current
    if (!modalEl) return

    const focusInput = () => {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    }

    if (modalEl.dataset.ui === 'visible') focusInput()

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ui') {
          if (modalEl.dataset.ui === 'visible') focusInput()
        }
      }
    })

    observer.observe(modalEl, { attributes: true, attributeFilter: ['data-ui'] })

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <x-modal className="group/search" data-id={id} data-body-lock ref={modalRef}>
      <div className="flex flex-col fixed inset-tl w-full h-fit z-100 pointer-events-none group-data-visible/search:pointer-events-auto -translate-y-full group-data-visible/search:translate-y-0 transition-all">
      <div className="flex flex-col lg:grid lg:grid-cols-main gap-x-gutter lg:gap-y-1.5 lg:px-margin overflow-y-scroll no-scrollbar overscroll-y-none px-margin border-b border-borders-default bg-secondary h-header items-center lg:border-r">
        <div className="search-logo bg-secondary lg:col-[1/3] lg:row-[1/2] hidden lg:flex justify-start items-center h-full lg:border-r lg:border-r-borders-default lg:border-r-solid">
          <div className="h-1.5 w-auto flex items-center justify-start aspect-[113/23] text-type-black-primary children:size-full lg-max:hidden">
          <Icon id="logo-wordmark" />
          </div>
        </div>
        <form action="/search" method="get" className="w-full h-header lg:px-margin  bg-secondary lg:col-[3/-1] lg:row-[1/2] relative">
        <span className="absolute left-0 lg:left-margin top-1/2 -translate-y-1/2 size-1 children:size-full">
          <Icon id="magnifying-glass" />
          </span>
            <div className="flex items-center justify-between gap-x-1.5 h-full pl-margin-double lg:pl-margin">
              <div className="flex items-center justify-start gap-x-1.5 flex-1">
              <SearchInput className="grow" ref={searchInputRef} redirect={redirect} onInput={handleUpdateValue} />
              {inputValue ? (
                  <button className="btn btn-text btn-s btn-transparent underline underline-offset-[6px] flex-shrink-0 whitespace-nowrap uppercase" onClick={resetInputValue} type="reset" aria-label="Clear search">
                    Clear
                  </button>
              ) : null}
                </div>
              <div className={'flex items-center justify-end gap-x-1.5 lg:w-[22%] lg:border-l lg:border-l-borders-default lg:border-l-solid'}>
                <button
                  className="btn btn-transparent btn-primary btn-s btn-text"
                  data-modal-parent={id}
                  data-modal-role="close"
                  ref={closeButtonRef}
                  onClick={handleClose}
                  aria-label="Close modal"
                >
                  <span className="paragraph-xs normal-case lg-max:hidden">Close Search</span>
                </button>
              </div>
              <button data-modal-parent={id}
                  data-modal-role="close"
                  ref={closeButtonRef}
                  onClick={handleClose}
                  aria-label="Close modal"
                  className="btn-m btn-square btn-transparent btn-primary children:size-full absolute right-0 top-1/2 -translate-y-1/2 size-1">
                    <Icon id="close" />
              </button>
            </div>
        </form>
      </div>

        <AthosAutocompleteProvider runOnMount={false} resultsPerPage={4}>
          <BeaconAnalyticsProvider type="autocomplete">
            <QuickSearchDropdown query={query} redirect={redirect} setRedirect={setRedirect} />
          </BeaconAnalyticsProvider>
        </AthosAutocompleteProvider>
      </div>
      <button
        className="fixed inset-tl size-full bg-type-black-primary/60 opacity-0 group-data-visible/search:opacity-100 pointer-events-none group-data-visible/search:pointer-events-auto cursor-n-resize z-90 transition-all"
        data-modal-parent={id}
        data-modal-role="close"
        aria-label="Close modal"
      ></button>
    </x-modal>
  )
}

export function WrappedQuickSearch({ id }) {
  return (
    <AthosAutocompleteProvider runOnMount={false}>
      <QuickSearch id={id} />
    </AthosAutocompleteProvider>
  )
}
